///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// My Places as layers-panel commands: search results and recently-used
/// entries resolve against this list, mirroring useSavedViewCommands.
/// Selecting one focuses the placemark on the globe (same path as the
/// My Places rows).
///

import { sendDatasetAction } from "@/redux/earthBrowserSlice"
import { cPlacemarkColors, cDefaultPlacemarkColor } from "@/redux/placemarksSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { MapPin } from "lucide-react"
import { useMemo } from "react"
import type { CommandItem } from "./commandTypes"

export function usePlacemarkCommands(): CommandItem[] {
	const dispatch = useAppDispatch()
	const placemarks = useAppSelector(state => state.placemarks.placemarks)

	return useMemo(() => {
		return Object.values(placemarks)
			.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
			.map(placemark => ({
				id: `placemark-${placemark.id}`,
				type: 'placemark' as const,
				title: placemark.name,
				subtitle: placemark.locations.length > 1
					? `My place · ${placemark.locations.length} locations`
					: 'My place',
				icon: (
					<MapPin
						className="w-4 h-4"
						style={{ color: cPlacemarkColors[placemark.color] ?? cPlacemarkColors[cDefaultPlacemarkColor] }}
					/>
				),
				keywords: [
					...placemark.tags,
					...placemark.locations
						.map(location => location.name)
						.filter((name): name is string => !!name)
				],
				action: () => {
					dispatch(sendDatasetAction({ type: "placemark", action: "selectId", id: placemark.id }))
				},
				enabled: true
			}))
	}, [placemarks, dispatch])
}
